import { Button, Form, success } from '@valid-eval/shared-react-components';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import TagsInput from 'components/Form/TagsInput';
import { email } from 'components/FormFields/validations';
import GenericModal from 'components/GenericModal';
import useBooleanFlag from 'utils/hooks/useBooleanFlag';
import { useShowErrorIfRejected } from 'utils/hooks/useShowErrorIfRejected';
import { useAddUsers } from 'data/features/eventsHooks';
import { Event } from 'data/features/eventsTypes';

type AddUserProps = {
  event: Event;
  userType: string;
};

type UserFormData = {
  emails: string[];
};

export const AddUser = ({ event, userType }: AddUserProps) => {
  const { t } = useTranslation();
  const userLabel = userType.charAt(0).toUpperCase() + userType.slice(1);
  const [show, open, close] = useBooleanFlag(false);
  const showErrorIfRejected = useShowErrorIfRejected();
  const { addUsers: addUsersToEvent, saving } = useAddUsers();
  const methods = useForm<UserFormData>({
    mode: 'all',
  });

  const onSubmit = async (data: UserFormData) => {
    const [result, hasError] = showErrorIfRejected(
      await addUsersToEvent(event.id, userType, data.emails),
    );
    if (!hasError) {
      if (result.payload?.emails_not_found?.length > 0) {
        methods.setError('emails', {
          message: t('event.form.add_user_error', {
            emails: result.payload.emails_not_found.join(', '),
            userType: userLabel,
          }),
        });
      } else {
        success(t('event.form.add_user_success', { userType: userLabel }));
        methods.reset();
        close();
      }
    }
  };

  const handleCancel = () => {
    methods.reset();
    close();
  };

  return (
    <>
      <GenericModal
        size="md"
        name={`add-${userType.toLowerCase()}`}
        show={show}
        onCancel={handleCancel}
        title={t('event.form.add_user', {
          userType: userLabel,
        })}
        body={
          <FormProvider {...methods}>
            <p>{t('event.form.add_user_instructions', { userType: userLabel })}</p>
            <Form onSubmit={methods.handleSubmit(onSubmit)}>
              <TagsInput
                name="emails"
                label="Emails"
                placeholder={`${userLabel} email`}
                validateBeforeAdd={(value) => !email(value)}
                required
                autoFocus
              />
              <button type="submit" hidden />
            </Form>
          </FormProvider>
        }
        onConfirm={methods.handleSubmit(onSubmit)}
        isButtonDisabled={saving}
        isCancelButtonDisabled={saving}
      />
      <Button variant="success" className="py-2 px-4" onClick={open}>
        <i className="fa-solid fa-plus" />
        {t('event.form.add_new')}
      </Button>
    </>
  );
};
